
import Vue from "vue";
import { get, zipObject, map, pick } from "lodash-es";
import * as raf from "raf";
import { useAuth, useCognitoAdmin } from "@/services";
import { Auth, Hub, I18n, Logger } from "aws-amplify";
import { CognitoSettings, IdentityProvider } from "@/types";
import { AuthProviderType } from "@/types/initSettings";
import GammaAuthenticator from "@/components/authenticator/GammaAuthenticator.vue";
import { AuthUIState } from "@/types/auth-ui";
import { useGammaAuthenticator } from "@/composeable/gamma-authenticator";
export default Vue.extend({
  name: "Login",
  components: { GammaAuthenticator },
  props: {
    to: {
      type: String,
      default: "",
    },
  },
  data: () => ({
    isLoggedIn: false,
    username: "",
    password: "",
    action: "",
    error: "",
    logger: {} as any,
    dest: {} as any,
    unsubscribeAuth: undefined as any | undefined,
    isCognitoSignin: false,
  }),
  computed: {
    loginQ(): string {
      return get(this.dest, "query.q") || "";
    },
    hasCustomProviders(): boolean {
      return !!(
        !(this.loginQ && this.password) &&
        this.cognitoSettings?.identityProviders?.length
      );
    },
    identityProviders(): IdentityProvider[] {
      return this.cognitoSettings?.identityProviders || [];
    },
    cognitoSettings(): CognitoSettings | null {
      return useAuth().cognitoSettings;
    },
    isUIStateSignedIn(): boolean {
      return useGammaAuthenticator().authUIState.value === AuthUIState.SignedIn;
    },
  },
  mounted() {
    this.dest = this.to ? JSON.parse(this.to) : {};

    raf(() => {
      (
        this.$el?.querySelectorAll('[class*="Input__inputLabel"]') || []
      ).forEach((el) => {
        el.innerHTML = el.innerHTML.replace("Username", "Email");
      });
      const input = this.$el?.querySelector(
        '[placeholder="Enter your Username"]'
      );
      if (input instanceof HTMLInputElement) {
        input.setAttribute("placeholder", "Enter your email address");
      }
    });
    this.unsubscribeAuth = Hub.listen("auth", async (data) => {
      const isSignedIn = data.payload.event === "signIn";
      if (isSignedIn) {
        this.unsubscribeAuth();
      }
      useAuth()
        .currentUserInfo()
        .then(() => {
          if (isSignedIn) {
            useCognitoAdmin().getCurrentUserGroups();
            if (["login", "auth"].includes(this.dest.path) || !this.dest.path) {
              this.dest = { path: "/" };
            }
            return this.$router.push(this.dest);
          }
        });
    });
    this.isCognitoSignin = !this.cognitoSettings?.identityProviders?.length;
    this.resolveAuth();
  },
  methods: {
    async resolveAuth() {
      let request: Record<string, any>;
      if (this.loginQ) {
        try {
          const pairs = atob(this.loginQ)
            .split("&")
            .map((o) => o.split("="));
          request = zipObject(map(pairs, "0"), map(pairs, "1"));
          Object.assign(
            this,
            pick(request, ["username", "password", "action"])
          );
        } catch {
          return false;
        }
      }

      if (this.username && this.password) {
        this.isCognitoSignin = true;
        await this.$nextTick();
        this.logger = new Logger("SignIn");
        try {
          await useGammaAuthenticator().signIn(this.username, this.password);
        } catch (e) {
          console.error(e);
          this.setError(e);
        }

        this.username = "";
        this.password = "";
      }
    },
    setError: function (e: any) {
      this.error = I18n.get(e.message || e);
      this.logger.error(this.error);
    },
    providerSignIn: function (provider: IdentityProvider) {
      if (provider.providerType === AuthProviderType.Cognito) {
        this.isCognitoSignin = true;
      } else {
        Auth.federatedSignIn({
          customProvider: provider.providerName || "",
        });
      }
    },
  },
  beforeDestroy() {
    if (this.unsubscribeAuth) this.unsubscribeAuth();
  },
});
